/**
 * RosterImageDisplay
 *
 * Renders the resolved team roster image (view-only) with signed-URL expiry
 * recovery so an expired URL never leaves a broken <img> mounted [SpecFlow I3].
 * On load failure we ask the parent to re-resolve once via `onPlainImageError`;
 * after that single retry is exhausted the parent flips to its error state.
 *
 * Web never authors or renders annotations — those are iPad-only notes drawn on
 * top of the roster. This view always shows the team's current roster image.
 */

interface RosterImageDisplayProps {
  teamName: string;
  /** The current roster image URL. */
  rosterImageUrl: string | null;
  /** Invoked when the roster image fails to load (one-shot recovery). */
  onPlainImageError: () => void;
  /**
   * Invoked when the roster image loads successfully, so the parent can reset
   * its one-shot retry budget — each fresh signed URL deserves its own recovery
   * attempt when it later expires.
   */
  onPlainImageLoad: () => void;
}

export function RosterImageDisplay({
  teamName,
  rosterImageUrl,
  onPlainImageError,
  onPlainImageLoad,
}: RosterImageDisplayProps) {
  if (!rosterImageUrl) return null;

  return (
    <figure className="roster-image-section__figure">
      <img
        src={rosterImageUrl}
        alt={`Roster image for ${teamName}`}
        className="roster-image-section__image"
        onError={onPlainImageError}
        onLoad={onPlainImageLoad}
        data-testid="roster-image"
      />
    </figure>
  );
}
